import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import Button from 'components/Buttons/Button'
import Table, { TableProps } from 'components/Table'
import Text from 'components/Text'
import { Flex, Toast } from 'components'
import { BasedOnBox } from './BasedOnBox'

import { MergeConfirmationModal } from 'modules/Leads/MergeLead/components/MergeConfirmationModal'
import { MergeLeadData } from 'modules/Leads/MergeLead/types'
import { LeadDetail } from 'store/types/lead'
import { MergeLeads } from 'services/types/lead'

import leadQuery from 'queries/lead'

import { DuplicateList, LeadDuplicateDetails } from 'services/types/lead'
import { LeadActivityType } from 'types/global'
import DuplicateInfo from './DuplicateInfo'

import useDuplicatesColumns from './use-duplicates-columns'
import { LEAD_DUPLICATE_TAB, Z_INDEX } from 'utils/constants'
import { useLeadMergeCheck } from './use-lead-merge-check'
import { getLeadRoute } from 'utils/url'
import { useReusableTab } from 'hooks/use-reusable-lead-tab'
import useLeadDetail from 'hooks/use-lead-detail'

const COLUMN_WIDTH = {
  lead_name: 100,
  lead_type_name: 100,
  lead_owner_name: 100,
  lead_status_name: 100,
  last_activity: 100,
  lead_in_date: 100,
  lead_id: 100,
}
const UNIQUE_TABLENAME = 'duplicatesTable'

type Props = {
  onClose: () => void
  onMarkedInvalid?: (leadId: string) => void
  data: DuplicateList
  leadDetail?: LeadDetail
  hideMergeButton?: boolean
  tableProps?: Pick<
    TableProps<LeadDuplicateDetails>,
    | 'pageNumber'
    | 'pageSize'
    | 'serverSide'
    | 'totalDataCount'
    | 'isLoading'
    | 'isFetching'
    | 'onSort'
    | 'onChangePagination'
  >
}

export const DUPLICATE_LIST_MODAL_ID = 'duplicate-list-modal' as const

const DuplicatesModal: React.FC<Props> = ({ data, leadDetail, onClose, hideMergeButton, tableProps }) => {
  const [modal, setModal] = useState<React.ReactNode | null>(null)
  const navigate = useNavigate()
  const { mergeLead, isLeadMergingErrored, leadMergingError } = leadQuery.useMergeLead()
  const { hasPermissionToMergeLead } = useLeadMergeCheck()
  const { openInReusableTab } = useReusableTab({ tabName: LEAD_DUPLICATE_TAB })
  const { data: leadDetailData } = useLeadDetail()

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const submitMergeLeadModal = (mergeLeadData: MergeLeadData) => async (payload: MergeLeads.Payload) => {
    // making modal status to loading
    setModal(
      <MergeConfirmationModal
        onSubmit={submitMergeLeadModal(mergeLeadData)}
        mergeLeadData={mergeLeadData}
        onClose={() => setModal(null)}
        isSubmitting={true}
      />,
    )

    await mergeLead(payload)

    if (isLeadMergingErrored) {
      Toast.toastMessage({
        message: leadMergingError?.message,
        variant: 'error',
      })

      return
    }

    Toast.toastMessage({
      message: 'lead_merged_success_lbl',
      variant: 'success',
    })

    onClose()
    setModal(null)
    navigate(getLeadRoute({ leadId: payload?.master_lead_id }))
  }

  const handleInfoClick = (leadId: string) => {
    const entry = data?.find((i: LeadDuplicateDetails) => i.lead_id === leadId)
    if (entry) {
      const isMergeable = !hideMergeButton && hasPermissionToMergeLead({ leadData: entry })

      setModal(
        <DuplicateInfo
          data={entry}
          onClose={() => {
            setModal(null)
          }}
          handleClickLink={() => {
            openInReusableTab(getLeadRoute({ leadId }))
          }}
          onMerge={() => {
            const mergeLeadData: MergeLeadData = {
              incomingLead: {
                leadId: leadDetail?.leadId || '',
                leadName: leadDetail?.leadName || '',
                leadStatusName: leadDetail?.leadStatusName || '',
                owner: {
                  id: leadDetail?.owner?.id || '',
                  fname: leadDetail?.owner?.fname || '',
                  lname: leadDetail?.owner?.lname || '',
                },
                ownerTeam: {
                  id: leadDetail?.ownerTeam?.id || '',
                  name: leadDetail?.ownerTeam?.name || '',
                },
                lastActivity: {
                  lastActivityTs: leadDetail?.lastActivity?.lastActivityTs,
                  lastActivityType: leadDetail?.lastActivity?.lastActivityType,
                  lastActivityStatus: leadDetail?.lastActivity?.lastActivityStatus,
                  lastActivityOutcome: leadDetail?.lastActivity?.lastActivityOutcome,
                },
                convertedBy: {
                  id: leadDetail?.convertedBy?.id || '',
                  fname: leadDetail?.convertedBy?.fname || '',
                  lname: leadDetail?.convertedBy?.lname || '',
                },
                convertedTs: leadDetail?.convertedTs,
                leadInDate: leadDetail?.leadInDate,
                leadTypeName: leadDetail?.leadTypeName,
              },
              selectedLead: {
                leadId: `${entry.lead_id}`,
                leadName: `${entry.lead_name}`,
                leadStatusName: `${entry.lead_status_name}`,
                owner: {
                  id: '',
                  fname: `${entry.lead_owner_name}`,
                  lname: '',
                },
                ownerTeam: {
                  id: '',
                  name: `${entry.lead_owner_team_name}`,
                },
                lastActivity: {
                  lastActivityTs: entry.last_activity.last_activity_ts,
                  lastActivityType: entry.last_activity.last_activity_type as LeadActivityType,
                  lastActivityStatus: entry.last_activity.last_activity_status,
                  lastActivityOutcome: '',
                },
                convertedBy: {
                  id: '',
                  fname: '',
                  lname: '',
                },
                convertedTs: '',
                leadInDate: `${entry.lead_in_date}`,
                leadTypeName: `${entry.lead_type_name}`,
              },
            }
            setModal(
              <MergeConfirmationModal
                onSubmit={async (payload) => {
                  submitMergeLeadModal(mergeLeadData)(payload)
                }}
                mergeLeadData={mergeLeadData}
                onClose={() => {
                  setModal(null)
                }}
                isSubmitting={false}
              />,
            )
          }}
          hideMergeButton={!isMergeable}
        />,
      )
    }
  }

  const { duplicateTableColumns } = useDuplicatesColumns({
    onInfoClick: handleInfoClick,
  })

  return (
    <>
      <Container>
        <Header>
          <Button icon="chevron-left" iconPosition="left" mr={24} onClick={handleClose} variant="ghost">
            back_btn
          </Button>
          <Text fontWeight="bold" fontSize={22} lineHeight="25px">
            duplicates_lbl
          </Text>
          <Flex padding="4px 8px" alignItems={'center'} gap="10px" width="max-content">
            <Text color="gray5">based on:</Text>
            <BasedOnBox isLeadDataLoading={false} leadDetail={leadDetailData} />
          </Flex>
        </Header>
        <Table
          columns={duplicateTableColumns}
          columnWidths={COLUMN_WIDTH}
          serverSide={tableProps?.serverSide ?? false}
          data={data}
          totalDataCount={tableProps?.totalDataCount ?? data.length}
          uniqueTableName={UNIQUE_TABLENAME}
          emptyText="duplicate_lead"
          isResponsiveHeight
          pageNumber={tableProps?.pageNumber ?? 1}
          pageSize={tableProps?.pageSize ?? 100}
          onSort={tableProps?.onSort}
          onChangePagination={tableProps?.onChangePagination}
          isLoading={tableProps?.isLoading}
          isFetching={tableProps?.isFetching}
        />
      </Container>
      {modal}
    </>
  )
}

const Container = styled.div`
  background-color: ${(p) => p.theme.colors.gray1};
  height: calc(100vh - 60px);
  left: 0;
  overflow: auto;
  position: fixed;
  top: 60px;
  width: 100%;
  z-index: ${Z_INDEX.window + 1};
`

const Header = styled.div`
  align-items: center;
  display: flex;
  margin: 21px 37px;
  position: relative;
  gap: 16px;
`

export default DuplicatesModal
